import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { getWindow } from 'ssr-window';

@Component({
  selector: 'app-authorized-scope',
  template: `
    <div class="authorized-scope-container">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>{{ statusMessage }}</p>
      </div>
    </div>
  `,
  styles: [`
    .authorized-scope-container {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    .loading-spinner {
      text-align: center;
      color: white;
    }

    .spinner {
      border: 4px solid rgba(255, 255, 255, 0.3);
      border-top: 4px solid white;
      border-radius: 50%;
      width: 50px;
      height: 50px;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    p {
      font-size: 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
  `]
})
export class AuthorizedScopeComponent implements OnInit {
  statusMessage: string = 'Authenticating...';

  constructor(
    private route: ActivatedRoute,
    private oauthService: OAuthService
  ) {}

  ngOnInit(): void {
    // Get query parameters
    this.route.queryParams.subscribe(params => {
      const requestedScope = params['requestedScope'];
      const redirectUri = params['redirect_uri'];

      console.log('AuthorizedScope - requestedScope:', requestedScope);
      console.log('AuthorizedScope - redirect_uri:', redirectUri);

      if (requestedScope === 'plugin' && redirectUri) {
        this.handlePluginAuth(redirectUri);
      } else {
        console.warn('Missing required parameters for plugin auth');
        this.statusMessage = 'Error: Missing required parameters';
      }
    });
  }

  private handlePluginAuth(redirectUri: string): void {
    try {
      // Check if user is already authenticated
      const hasValidToken = this.oauthService.hasValidAccessToken();
      
      if (hasValidToken) {
        // User is already authenticated, redirect immediately
        this.redirectWithAuthData(redirectUri);
      } else {
        // Store redirect info in sessionStorage and trigger login
        sessionStorage.setItem('plugin_requested_scope', 'plugin');
        sessionStorage.setItem('plugin_redirect_uri', decodeURIComponent(redirectUri));

        localStorage.setItem('plugin_requested_scope', 'plugin');
        localStorage.setItem('plugin_redirect_uri', decodeURIComponent(redirectUri));
        
        console.log('User not authenticated, redirecting to login...');
        // Redirect to login with state to return here
        this.oauthService.initLoginFlow('/authorized-scope');
      }
    } catch (error) {
      console.error('Error in handlePluginAuth:', error);
      getWindow().location.href = '/';
    }
  }

  private redirectWithAuthData(redirectUri: string): void {
    try {
      // Get access token
      const accessToken = this.oauthService.getAccessToken();
      if (!accessToken) {
        console.warn('No access token available');
        this.statusMessage = 'Error: No access token available';
        return;
      }

      // Get token expiration
      const tokenExpiration = this.oauthService.getAccessTokenExpiration();
      const expiresAt = tokenExpiration || Date.now() + 60 * 60 * 1000;

      // Get user email from token claims
      const claims: any = this.oauthService.getIdentityClaims();
      const email = claims?.email || claims?.preferred_username || '';

      // Build redirect URL with parameters
      const decodedUri = decodeURIComponent(redirectUri);
      let finalRedirectUrl: string;
      
      if (decodedUri.startsWith('chrome-extension://')) {
        // Chrome extensions can't be redirected to directly (ERR_BLOCKED_BY_CLIENT)
        // Instead, store auth data in localStorage with a unique key the extension can read
        const authData = {
          access_token: accessToken,
          expires_at: expiresAt,
          email: email,
          timestamp: Date.now()
        };
        
        // Store with a key the extension can access
        localStorage.setItem('atalef_extension_auth', JSON.stringify(authData));
        
        // Build the URL for display purposes
        const separator = decodedUri.includes('?') ? '&' : '?';
        const params = new URLSearchParams();
        params.set('access_token', accessToken);
        params.set('expires_at', expiresAt.toString());
        params.set('email', email);
        finalRedirectUrl = `${decodedUri}${separator}${params.toString()}`;
        
        console.log('Chrome extension auth - stored data in localStorage');
        console.log('Extension should read from: atalef_extension_auth');
        
        // Update status message
        this.statusMessage = 'Authentication successful! Closing window...';
        
        // Clear plugin session storage
        sessionStorage.removeItem('plugin_redirect_uri');
        sessionStorage.removeItem('plugin_requested_scope');
        
        // Close the window after a short delay
        setTimeout(() => {
          getWindow().close();
        }, 2000);
        
      } else {
        // Regular URL - use standard redirect
        const redirectUrl = new URL(decodedUri);
        redirectUrl.searchParams.set('access_token', accessToken);
        redirectUrl.searchParams.set('expires_at', expiresAt.toString());
        redirectUrl.searchParams.set('email', email);
        finalRedirectUrl = redirectUrl.toString();
        
        console.log('Redirecting to:', finalRedirectUrl);
        
        // Update status message
        this.statusMessage = 'Redirecting...';
        
        // Clear session storage
        sessionStorage.removeItem('plugin_redirect_uri');
        sessionStorage.removeItem('plugin_requested_scope');
        
        // Redirect to the callback URL
        getWindow().location.assign(finalRedirectUrl);
      }
    } catch (error) {
      console.error('Error building redirect with auth data:', error);
      this.statusMessage = 'Error: ' + (error as Error).message;
    }
  }
}
